<script setup lang="ts">
/**
 * 色板页签 —— 对齐参考产品（svgeditai.com）的工作流：
 *   自动提取颜色（带元素计数） → 逐行映射到品牌色 → 一键应用映射（一条可撤销命令）
 *
 * 交互约定：
 *  · **悬停**颜色行 = 画布上预览（虚线框住命中元素 + 其余压暗），**不动选中集**
 *  · **点行右侧的目标色块/箭头** = 弹出悬浮框：品牌色板 + 自定义颜色
 *  · **选色立刻写入文档**（一行一次、可 Ctrl+Z 逐条撤回；连续调色合并成一条）
 *  · 右栏「你的品牌色」**只作展示**；「智能映射」按角色配对并立刻应用
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useEditor } from '@/composables/useEditor';
import { roleLabel, smartMapping, type ColorUsage } from '@/core/palette';
import { setInspectorTab } from '@/stores/editor';
import { addBrandColor, brandColors, clearMapping, palette, setHint, setMapping } from '@/stores/palette';

const editor = useEditor();

/**
 * 已映射源色的"冻结"快照。
 * 为什么需要：颜色一被改写，文档里就没有这个色了 —— 若列表照实时提取，用户点完色
 * 那一行会当场消失（像丢了东西）。所以映射过的源色保留它原来的计数与角色继续显示。
 */
const frozen = ref<Record<string, ColorUsage>>({});

/** 列表 = ①已映射的源色（含已从文档消失的） + ②文档里当前的颜色（排除映射的目标色） */
const usages = computed<ColorUsage[]>(() => {
  const live = editor.colorUsages.value;
  const targets = new Set(Object.values(palette.mapping));
  const out: ColorUsage[] = [];
  const seen = new Set<string>();
  for (const hex of Object.keys(palette.mapping)) {
    const snap = frozen.value[hex] ?? live.find((u) => u.hex === hex);
    if (snap && !seen.has(snap.hex)) {
      out.push(snap);
      seen.add(snap.hex);
    }
  }
  for (const u of live) {
    if (seen.has(u.hex) || targets.has(u.hex)) continue;
    out.push(u);
    seen.add(u.hex);
  }
  return out.sort((a, b) => b.count - a.count);
});

/** 冻结某一行的快照（选色时调用：此刻文档里还有这个色，计数最准） */
const freeze = (hex: string): void => {
  const snap = usages.value.find((u) => u.hex === hex);
  if (snap) frozen.value = { ...frozen.value, [hex]: snap };
};
const mappedCount = computed(() => Object.keys(palette.mapping).length);
const canApply = computed(() => mappedCount.value > 0 && usages.value.length > 0);

const newHex = ref('#6366f1');
const newLabel = ref('');
const customHex = ref('#000000');
/** 悬浮框位置（fixed 定位，Teleport 到 body，避免被面板 overflow 裁掉） */
const popAt = ref({ left: 0, top: 0 });
const POP_W = 236;
const POP_H = 200; // 估算高度：只为"下方放不下就向上弹"

const targetOf = (hex: string): string | null => palette.mapping[hex] ?? null;

/** 悬停颜色行：临时预览（画布虚线框 + 其余压暗），选中集原封不动 */
const hover = (hex: string | null): void => {
  palette.hover = hex;
  editor.previewColor(hex);
};

/** 点行右侧目标色块/箭头 → 开/关悬浮框（贴触发器右缘，下方放不下则向上弹） */
const togglePicker = (hex: string, e: MouseEvent): void => {
  if (palette.pickerFor === hex) {
    palette.pickerFor = null;
    return;
  }
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const below = r.bottom + 6;
  popAt.value = {
    left: Math.max(8, Math.min(r.right - POP_W, window.innerWidth - POP_W - 8)),
    top: below + POP_H > window.innerHeight ? Math.max(8, r.top - POP_H - 6) : below,
  };
  customHex.value = targetOf(hex) ?? hex;
  palette.pickerFor = hex;
};

/**
 * 点品牌色：立刻写入文档，但**悬浮框保持打开** ——
 * 选色即写入之后，连着点几个色看画布变化才是正常用法（一点就收起来很难受，用户反馈过）。
 * 关闭靠：点外部 / Esc / 再点一次那个触发器 / 点「应用映射」。
 */
const choose = (hex: string): void => {
  const src = palette.pickerFor;
  if (!src) return;
  freeze(src);
  setMapping(src, hex);
  const ok = editor.pickColorMapping(src, hex);
  setHint(`${src} → ${hex}${ok ? '（已写入，Ctrl+Z 可撤回）' : '（文档里已无此色，仅记录）'}`);
};

const onCustom = (e: Event): void => {
  const hex = (e.target as HTMLInputElement).value;
  customHex.value = hex;
  const src = palette.pickerFor;
  if (!src) return;
  freeze(src);
  setMapping(src, hex);
  // 连续拖动取色 → merge 合并成一条历史，松手只占一次 Ctrl+Z
  editor.pickColorMapping(src, hex, { merge: true });
  setHint(`${src} → ${hex}（自定义 · 实时写入）`);
};

const clearRow = (): void => {
  const src = palette.pickerFor;
  if (!src) return;
  palette.pickerFor = null;
  // 已经写进文档的：按原字面量还回去（一条可撤销命令），再把这一行放回实时提取
  const reverted = editor.revertColorPick(src);
  setMapping(src, null);
  if (reverted) {
    const next = { ...frozen.value };
    delete next[src];
    frozen.value = next;
  }
  setHint(reverted ? `${src} 已改回原色，映射已清除` : `已清除 ${src} 的映射`);
};

/* 关闭时机：点外部 / Esc（捕获阶段拦下，别让编辑器的 Esc 顺手清掉选中）*/
const onDocDown = (e: Event): void => {
  const t = e.target;
  // e.target 不一定是 Element（document/window 也会派发事件）—— 不能直接 .closest
  if (t instanceof Element && (t.closest('.pal-pop') || t.closest('[data-field="pal-target"]'))) return;
  palette.pickerFor = null;
};
const onDocKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return;
  e.stopPropagation();
  palette.pickerFor = null;
};

watch(
  () => palette.pickerFor,
  (open) => {
    if (open) {
      document.addEventListener('pointerdown', onDocDown, true);
      document.addEventListener('keydown', onDocKey, true);
    } else {
      document.removeEventListener('pointerdown', onDocDown, true);
      document.removeEventListener('keydown', onDocKey, true);
    }
  },
);
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown, true);
  document.removeEventListener('keydown', onDocKey, true);
});

const runSmart = (): void => {
  if (usages.value.length === 0) {
    setHint('文档里没有可映射的颜色');
    return;
  }
  const mapping = smartMapping(usages.value, brandColors());
  for (const hex of Object.keys(mapping)) freeze(hex);
  palette.mapping = mapping;
  palette.pickerFor = null;
  // ★ 立刻应用（一条命令），不用再点"应用映射"
  const ok = editor.applyColorMapping();
  setHint(ok ? `智能映射已应用：${Object.keys(mapping).length} 种颜色` : '智能映射已配对（没有可改写的颜色）');
};

const apply = (selectionOnly: boolean): void => {
  palette.menuOpen = false;
  palette.pickerFor = null;
  editor.applyColorMapping({ selectionOnly });
};

const add = (): void => {
  addBrandColor(newLabel.value.trim() || newHex.value, newHex.value);
  newLabel.value = '';
  setHint(`已添加品牌色 ${newHex.value}`);
};

const clear = (): void => {
  clearMapping();
  palette.pickerFor = null;
  frozen.value = {};
  setHint('已清除映射表（文档改动请用 Ctrl+Z 撤回）');
};

const close = (): void => {
  setInspectorTab('props');
};

const onNewHex = (e: Event): void => {
  newHex.value = (e.target as HTMLInputElement).value;
};
</script>

<template>
  <div class="pal">
    <div class="pal-head">
      <span class="pal-icon" aria-hidden="true">🎨</span>
      <b>色板</b>
      <span class="panel-note">· 从你的 SVG 自动提取</span>
      <span class="grow" />
      <button class="ghost-btn" data-field="pal-clear" :disabled="mappedCount === 0" @click="clear">清除</button>
      <div class="pal-apply">
        <button class="primary-btn" data-field="pal-apply" :disabled="!canApply" @click="apply(false)">重新应用</button>
        <button
          class="primary-btn pal-caret"
          title="更多方式"
          :disabled="!canApply"
          @click="palette.menuOpen = !palette.menuOpen"
        >
          ▾
        </button>
        <div v-if="palette.menuOpen" class="pal-menu">
          <button data-field="pal-apply-all" @click="apply(false)">应用到整篇文档</button>
          <button data-field="pal-apply-sel" :disabled="editor.selection.value.length === 0" @click="apply(true)">
            仅应用到选中元素（{{ editor.selection.value.length }}）
          </button>
        </div>
      </div>
      <button class="ghost-btn" title="关闭色板" @click="close">✕</button>
    </div>

    <div class="pal-pack">
      <span class="panel-note">品牌包</span>
      <div class="pal-packbox">
        <span class="pal-dots">
          <i v-for="c in brandColors().slice(0, 4)" :key="c.id" :style="{ background: c.hex }" />
        </span>
        <select v-model="palette.activePackId" data-field="pal-pack" aria-label="品牌包">
          <option v-for="pack in palette.packs" :key="pack.id" :value="pack.id">{{ pack.name }}</option>
        </select>
      </div>
    </div>

    <div class="pal-cols">
      <section class="pal-col">
        <div class="pal-colhead">
          <span>SVG 中的颜色</span>
          <span class="grow" />
          <span class="chip" data-field="pal-count">{{ usages.length }} 种</span>
        </div>
        <div class="pal-list">
          <div
            v-for="usage in usages"
            :key="usage.hex"
            class="pal-row"
            :class="{ 'is-preview': palette.hover === usage.hex, 'is-mapped': targetOf(usage.hex) !== null }"
            data-field="pal-row"
            :data-hex="usage.hex"
            :title="`${usage.hex} · ${usage.count} 个元素 · ${roleLabel(usage)}`"
            @mouseenter="hover(usage.hex)"
            @mouseleave="hover(null)"
          >
            <span class="pal-swatch" :style="{ background: usage.hex }" />
            <span class="pal-meta">
              <b>{{ usage.hex }}</b>
              <i>{{ usage.count }} 个元素</i>
            </span>
            <button
              class="pal-pick"
              :class="{ 'is-open': palette.pickerFor === usage.hex }"
              data-field="pal-target"
              :title="targetOf(usage.hex) ? `目标 ${targetOf(usage.hex)}：点击换一个颜色` : '点击选择颜色（品牌色板 / 自定义）'"
              @click.stop="togglePicker(usage.hex, $event)"
              @focus="hover(usage.hex)"
              @blur="hover(null)"
            >
              <span class="pal-arrow" aria-hidden="true">→</span>
              <span
                class="pal-target"
                :class="{ 'is-empty': targetOf(usage.hex) === null }"
                :style="targetOf(usage.hex) ? { background: targetOf(usage.hex) as string } : {}"
              />
            </button>
          </div>
          <p v-if="usages.length === 0" class="empty-hint">文档里还没有可映射的颜色</p>
        </div>
      </section>

      <section class="pal-col">
        <div class="pal-colhead">
          <span>你的品牌色</span>
        </div>
        <div class="pal-brand">
          <div
            v-for="color in brandColors()"
            :key="color.id"
            class="pal-card is-static"
            data-field="pal-brand"
            :data-hex="color.hex"
          >
            <span class="pal-swatch" :style="{ background: color.hex }" />
            <span class="pal-meta">
              <b>{{ color.name }}</b>
              <i>{{ color.hex }}</i>
            </span>
          </div>
        </div>

        <div class="pal-addcard">
          <div class="pal-addhead">添加品牌色</div>
          <div class="pal-add">
            <input
              class="pal-newswatch"
              type="color"
              :value="newHex"
              aria-label="新品牌色颜色"
              @input="onNewHex"
            />
            <div class="input">
              <input
                data-field="pal-new-label"
                placeholder="Label"
                spellcheck="false"
                :value="newLabel"
                @input="newLabel = ($event.target as HTMLInputElement).value"
                @keydown.enter.prevent="add"
              />
            </div>
            <button class="pal-plus" data-field="pal-add" title="添加品牌色" @click="add">＋</button>
          </div>
        </div>

        <button class="pal-drop" disabled>
          <span class="pal-dropicon" aria-hidden="true">🖼</span>
          <span>点击或拖入 Logo 以提取颜色<br />或拖入 Figma .json 变量文件</span>
        </button>
      </section>
    </div>

    <div class="pal-smart">
      <div class="pal-smart-top">
        <span class="pal-smart-icon" aria-hidden="true">✨</span>
        <span class="pal-smart-main">
          <b>智能映射</b>
          <i>按视觉角色对应</i>
        </span>
        <span class="grow" />
        <button class="primary-btn pal-smart-go" data-field="pal-smart" @click="runSmart">智能映射</button>
      </div>
      <label class="pal-toggle">
        <input
          type="checkbox"
          data-field="pal-keep"
          :checked="palette.keepLuminance"
          @change="palette.keepLuminance = ($event.target as HTMLInputElement).checked"
        />
        <span>保留亮度关系</span>
      </label>
    </div>

    <p class="panel-note pal-hint" data-field="pal-hint">
      {{
        palette.hint ||
          (mappedCount
            ? `${mappedCount} 种颜色已映射，点「应用映射」写入文档`
            : '选色即写入文档（Ctrl+Z 可撤回）· 悬停行可预览它的元素 · 点行右侧色块选色')
      }}
    </p>

    <!-- 悬浮框：Teleport 到 body，避免被面板的 overflow / 层叠上下文裁切 -->
    <Teleport to="body">
      <div
        v-if="palette.pickerFor"
        class="pal-pop"
        data-field="pal-pop"
        :style="{ left: `${popAt.left}px`, top: `${popAt.top}px`, width: `${POP_W}px` }"
      >
        <div class="pal-pop-title">品牌色板</div>
        <div class="pal-pop-grid">
          <button
            v-for="color in brandColors()"
            :key="color.id"
            class="pal-pop-swatch"
            :class="{ 'is-active': targetOf(palette.pickerFor) === color.hex }"
            data-field="pal-pop-brand"
            :data-hex="color.hex"
            :style="{ background: color.hex }"
            :title="`${color.name} ${color.hex}`"
            @click="choose(color.hex)"
          />
        </div>
        <div class="pal-pop-div" />
        <div class="pal-pop-custom">
          <span>自定义颜色</span>
          <span class="grow" />
          <span class="pal-pop-hex" data-field="pal-pop-hex">{{ customHex }}</span>
          <input
            class="pal-colorinput"
            type="color"
            data-field="pal-pop-custom"
            :value="customHex"
            aria-label="自定义颜色"
            @input="onCustom"
          />
        </div>
        <button
          v-if="palette.pickerFor && targetOf(palette.pickerFor)"
          class="pal-pop-clear"
          data-field="pal-pop-clear"
          @click="clearRow"
        >
          清除这一行的映射
        </button>
      </div>
    </Teleport>
  </div>
</template>
